/*
 * Planned muscle volume of builder-shaped workouts, computed locally so the
 * builders update instantly. This is a deliberate MIRROR of the backend
 * canonical rules, kept in lockstep by a parity test against the backend
 * engine (plannedMuscleVolume.parity.test.js):
 * - credit: backend/src/domain/muscleVolume/contributionLevels.js
 * - engine: backend/src/domain/muscleVolume/muscleVolumeEngine.js
 * - set eligibility: backend/src/domain/muscleVolume/volumeEligibility.js
 * - exercise rules: backend/src/domain/insights/muscleVolumeReadModel.js
 * - parents: backend/src/domain/muscleVolume/muscleParents.js
 *
 * The result has the Insights volume body shape (`nodes`, `parents`), so the
 * same group helpers present it. bodyParts is never read: an exercise without a
 * valid contribution is unclassified and kept out of every total.
 */

import {
  buildMuscleGroups,
  muscleLabel,
  readMuscleValue,
  VOLUME_BASIS,
} from "../insights/domain/insightsPresentation";

export const SET_CREDIT_BY_LEVEL = Object.freeze({ DIRECT: 1, INDIRECT: 0.5, MINOR: 0 });

export const CONTRIBUTION_NODES = Object.freeze([
  "chest", "lats", "upper_back", "traps", "front_delts", "side_delts", "rear_delts",
  "biceps", "triceps", "forearms", "quads", "hamstrings", "glute_max", "glute_med",
  "adductors", "gastrocnemius", "soleus", "abs", "obliques", "lower_back",
]);

export const MUSCLE_PARENTS = Object.freeze([
  { parent: "back", children: ["lats", "upper_back", "traps"] },
  { parent: "shoulders", children: ["front_delts", "side_delts", "rear_delts"] },
  { parent: "glutes", children: ["glute_max", "glute_med"] },
  { parent: "calves", children: ["gastrocnemius", "soleus"] },
  { parent: "core", children: ["abs", "obliques", "lower_back"] },
]);

const NODE_SET = new Set(CONTRIBUTION_NODES);
const EXCLUDED_TRAINING_TYPES = new Set(["cardio", "warmup", "mobility"]);

function isWarmupSet(set) {
  const setType = set?.setType ?? set?.persistence?.setType;
  return String(setType || "").toUpperCase() === "WARMUP";
}

function countQualifyingSets(sets) {
  return (Array.isArray(sets) ? sets : []).filter((set) => set && !isWarmupSet(set)).length;
}

/*
 * Builder / viewer workouts → `{ exerciseId, sets }` entries. Blocks the
 * physical set counter skips (no exercise picked yet) are skipped here too;
 * cardio carries no muscle volume. A superset lane counts its own sets within
 * the block's round count, one entry per lane.
 */
export function builderVolumeEntries(workouts) {
  const entries = [];
  for (const workout of workouts || []) {
    for (const block of workout?.blocks || []) {
      if (block?.type === "single") {
        if (!block.exerciseId) continue;
        const sets = countQualifyingSets(block.sets);
        if (sets > 0) entries.push({ exerciseId: block.exerciseId, sets });
        continue;
      }
      if (block?.type !== "superset") continue;
      const rounds = Math.max(0, Number(block.sets) || 0);
      for (const exercise of block.exercises || []) {
        if (!exercise?.exerciseId) continue;
        const laneSets = Array.isArray(exercise.sets) ? exercise.sets.slice(0, rounds) : [];
        const sets = countQualifyingSets(laneSets);
        if (sets > 0) entries.push({ exerciseId: exercise.exerciseId, sets });
      }
    }
  }
  return entries;
}

function isValidContribution(contribution) {
  if (!contribution || typeof contribution !== "object" || Array.isArray(contribution)) return false;
  const pairs = Object.entries(contribution);
  return pairs.length > 0 && pairs.every(
    ([node, level]) => NODE_SET.has(node) && Object.prototype.hasOwnProperty.call(SET_CREDIT_BY_LEVEL, level)
  );
}

function lookup(catalog, exerciseId) {
  if (!catalog || exerciseId == null) return null;
  return (catalog instanceof Map ? catalog.get(exerciseId) : catalog[exerciseId]) || null;
}

/*
 * entries: `{ exerciseId, sets }`; catalog: exerciseId → { trainingType,
 * muscleContribution } (the /api/exercises/muscle-contributions items).
 */
export function computePlannedMuscleVolume(entries, catalog) {
  const byNode = new Map(CONTRIBUTION_NODES.map((node) => [
    node, { node, directSets: 0, indirectSets: 0, weightedSets: 0 },
  ]));
  let qualifyingSets = 0;
  let unclassifiedSets = 0;

  for (const entry of entries || []) {
    const sets = Number(entry?.sets) || 0;
    if (sets <= 0) continue;
    const exercise = lookup(catalog, entry.exerciseId);
    const trainingType = String(exercise?.trainingType || "").trim().toLowerCase();
    if (exercise && EXCLUDED_TRAINING_TYPES.has(trainingType)) continue;
    if (!exercise || !isValidContribution(exercise.muscleContribution)) {
      unclassifiedSets += sets;
      continue;
    }
    qualifyingSets += sets;
    for (const [node, level] of Object.entries(exercise.muscleContribution)) {
      if (level === "MINOR") continue;
      const row = byNode.get(node);
      if (level === "DIRECT") row.directSets += sets;
      else row.indirectSets += sets;
      row.weightedSets += sets * SET_CREDIT_BY_LEVEL[level];
    }
  }

  const nodes = [...byNode.values()];
  // A parent is the SUM of its children (overlap intended, as in Insights).
  const parents = MUSCLE_PARENTS.map(({ parent, children }) => {
    const total = { parent, children: [...children], directSets: 0, indirectSets: 0, weightedSets: 0 };
    for (const child of children) {
      const row = byNode.get(child);
      total.directSets += row.directSets;
      total.indirectSets += row.indirectSets;
      total.weightedSets += row.weightedSets;
    }
    return total;
  });

  return { qualifyingSets, nodes, parents, unclassifiedSets };
}

/*
 * A volume body → MuscleDistribution items, one per display group (a parent
 * or a standalone node, never both, so nothing is counted twice). Shared by the
 * locally computed and the backend-computed volumes.
 */
export function toMuscleVolumeItems(volume) {
  if (!volume) return [];
  return buildMuscleGroups(volume).map(({ key }) => ({
    key,
    label: muscleLabel(key),
    weightedSets: readMuscleValue(volume, key, VOLUME_BASIS.WEIGHTED),
    directSets: readMuscleValue(volume, key, VOLUME_BASIS.DIRECT),
    indirectSets: readMuscleValue(volume, key, VOLUME_BASIS.INDIRECT),
  }));
}
